import { UseMutationOptions, UseMutationResult } from 'react-query';
import { CreatePMRegistrationLinkRequest, PaymentPlanQueries } from '@frontend/api-payment-plans';
import { PersonsV3 } from '@frontend/api-person';
import { isWeaveUser } from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';
import { PaymentMethodsBaseApiMethods } from '../types/api.types';
import { DeleteMethodOptions } from './use-delete-payment-method';

export type UsePaymentMethodsStripeApiProps = {
  isPublic?: boolean;
};

export interface PaymentMethodsStripeApi extends PaymentMethodsBaseApiMethods {
  invalidatePaymentMethodsByPerson: (patientId?: string, locationId?: string) => void;
  useCreatePMRegistrationLink: (
    options?: UseMutationOptions<unknown, unknown, CreatePMRegistrationLinkRequest>
  ) => UseMutationResult<unknown, unknown, CreatePMRegistrationLinkRequest, unknown>;
  canUserAccessAch: (isRegularLocation: boolean) => boolean;
}

export const usePaymentMethodsStripeApi = ({ isPublic }: UsePaymentMethodsStripeApiProps): PaymentMethodsStripeApi => {
  const getPublicImplementation = (): PaymentMethodsStripeApi => {
    return {
      usePaymentPlansInvalidation: () => {
        console.warn('unimplemented method');
        return {
          invalidatePaymentMethodsByPerson: () => {},
        };
      },
      useDeletePaymentMethod: (_options: UseMutationOptions<unknown, unknown, DeleteMethodOptions>) => {
        console.warn('unimplemented method');
        return {} as UseMutationResult<
          unknown,
          unknown,
          {
            paymentMethodId: string;
            locationId: string;
          },
          unknown
        >;
      },
      useGetPaymentMethodsByPerson: () => {
        console.warn('unimplemented method');
        return {
          data: undefined,
          isLoading: false,
          isError: false,
          error: null,
        } as any;
      },
      useQueryLegacyPerson: () => {
        console.warn('unimplemented method');
        return {
          data: undefined,
          isLoading: false,
          isError: false,
          error: null,
        } as any;
      },
      invalidatePaymentMethodsByPerson: () => {
        console.warn('unimplemented method');
      },
      useCreatePMRegistrationLink: (
        _options?: UseMutationOptions<unknown, unknown, CreatePMRegistrationLinkRequest>
      ) => {
        console.warn('unimplemented method');
        return {} as UseMutationResult<unknown, unknown, CreatePMRegistrationLinkRequest, unknown>;
      },
      canUserAccessAch: () => {
        console.warn('unimplemented method');
        return false;
      },
    };
  };

  const { invalidatePaymentMethodsByPerson } = PaymentPlanQueries.usePaymentPlansInvalidation();

  const getAuthenticatedImplementation = (): PaymentMethodsStripeApi => {
    return {
      invalidatePaymentMethodsByPerson,
      usePaymentPlansInvalidation: PaymentPlanQueries.usePaymentPlansInvalidation,
      useDeletePaymentMethod: PaymentPlanQueries.useDeletePaymentMethod,
      useGetPaymentMethodsByPerson: PaymentPlanQueries.useGetPaymentMethodsByPerson,
      useQueryLegacyPerson: ({
        locationId,
        personId,
      }: {
        locationId: string | undefined;
        personId: string | undefined;
      }) => {
        return PersonsV3.PersonQueries.useGetPersonLegacyQuery(
          { locationIds: [locationId ?? ''], personId: personId ?? '' },
          { enabled: !!personId }
        );
      },
      useCreatePMRegistrationLink: PaymentPlanQueries.useCreatePMRegistrationLink,
      canUserAccessAch: (isRegularLocation) =>
        !(http.baseUrl.includes('api.weaveconnect.com') && isWeaveUser() && isRegularLocation),
    };
  };
  return isPublic ? getPublicImplementation() : getAuthenticatedImplementation();
};
